/* =============================================================================
   VIEWS / CUSTOMER-CANCEL — Storno-Flow für anonyme Gäste
   =============================================================================
   Behandelt `#cancel=<token>` im URL-Hash. Unterstützt zwei Token-Typen:
     • Reservation-Token (Präfix "c", aus create_customer_reservation)
     • Order-Token       (Präfix "o", aus create_customer_order)
   Das Präfix entscheidet, welche RPC verwendet wird; bei unklarem Präfix
   probieren wir nacheinander (Order zuerst, weil es im neuen Flow primär ist).

   Alle RPCs sind SECURITY DEFINER und schreiben in derselben Transaktion
   einen audit_log-Eintrag. Anonyme Clients haben keinerlei direkten
   Tabellen-Zugriff auf reservations oder orders.
============================================================================ */

import { htmlEscape, fmtDate, fmtEUR } from '../utils/helpers.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { settings: {} };
}

/** Erkennt den Token-Typ aus dem Präfix. "o"=Order, "c"=Reservation,
 *  alles andere bekommt 'unknown' und löst einen Fallback-Probier-Pfad aus. */
function tokenKind(token) {
  if (typeof token !== 'string' || token.length === 0) return 'unknown';
  const first = token.charAt(0).toLowerCase();
  if (first === 'o') return 'order';
  if (first === 'c') return 'reservation';
  return 'unknown';
}

/** Lädt den Datensatz hinter einem Token. Liefert
 *  { kind: 'order'|'reservation', data: <r> } oder null falls nichts gefunden. */
async function lookupByToken(token) {
  const kind = tokenKind(token);
  if (kind === 'order') {
    const o = await window.App.storage.orders.getByToken(token);
    if (o) return { kind: 'order', data: o };
  } else if (kind === 'reservation') {
    const r = await window.App.storage.reservations.getByToken(token);
    if (r) return { kind: 'reservation', data: r };
  } else {
    // Unbekanntes Präfix: erst Order, dann Reservation probieren.
    try {
      const o = await window.App.storage.orders.getByToken(token);
      if (o) return { kind: 'order', data: o };
    } catch (_) {}
    try {
      const r = await window.App.storage.reservations.getByToken(token);
      if (r) return { kind: 'reservation', data: r };
    } catch (_) {}
  }
  return null;
}

/** Wenn `#cancel=<token>` in der URL steht: View wechseln + RPC laden.
 *
 *  ⚠ WICHTIG (Audit 2026-05-20, Finding M-09 — Prefetch-Schutz):
 *  Der GET-Pfad lädt AUSSCHLIESSLICH Lese-Daten via `lookupByToken` und
 *  rendert die Detail-Card. Ein expliziter Klick auf den Storno-Button
 *  ruft erst dann `performCustomerCancel` (RPC `cancel_*_by_token`).
 *
 *  Dieses Zwei-Stufen-Pattern ist Pflicht, weil Mail-Clients (Gmail, Apple
 *  Mail, Outlook, Spam-Filter) Links beim Empfang prefetchen. Würde der
 *  GET-Link DIREKT stornieren, gäbe es ungewollte Stornos. NIEMALS eine
 *  Mutation hier im checkCancelRequestFromUrl-Pfad oder im
 *  renderCancelView-Pfad einbauen — diese laufen unter Prefetch-Bedingungen. */
export function checkCancelRequestFromUrl() {
  const token = _parseCancelTokenFromHash(window.location.hash);
  if (!token) return false;
  renderCancelLoading();
  if (typeof window.switchView === 'function') window.switchView('cancel');
  (async () => {
    try {
      const hit = await lookupByToken(token);
      renderCancelView(hit, token);
    } catch (e) {
      console.error('[checkCancelRequestFromUrl] lookup:', e);
      renderCancelView(null, token);
    }
  })();
  return true;
}

/** Loading-State, während die RPC läuft. */
export function renderCancelLoading() {
  const wrap = document.getElementById('cancel-card');
  if (!wrap) return;
  wrap.innerHTML = `
    <h2>Reservierung wird geladen ...</h2>
    <p style="color:var(--ink-soft);">Einen Moment bitte.</p>
  `;
}

/** Cancel-Card rendern: je nach Treffer (Reservation/Order) und Status. */
export function renderCancelView(hit, token) {
  const state = getState();
  const wrap = document.getElementById('cancel-card');
  if (!wrap) return;
  if (!state.settings.allowCustomerCancel) {
    wrap.innerHTML = `
      <h2>Online-Stornierung deaktiviert</h2>
      <p>Bitte melde dich telefonisch oder per E-Mail beim Restaurant, um deine Reservierung oder Bestellung zu stornieren.</p>
      <p style="color:var(--ink-soft);">Telefon: ${htmlEscape(state.settings.restaurantPhone || '—')}<br>E-Mail: ${htmlEscape(state.settings.restaurantEmail || '—')}</p>
      <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück</a>`;
    return;
  }
  if (!hit) {
    wrap.innerHTML = `
      <h2>Vorgang nicht gefunden</h2>
      <p>Der Stornierungs-Link ist ungültig oder bereits gelöscht.</p>
      <p style="color:var(--ink-soft);">Bitte ruf uns kurz an, dann erledigen wir das telefonisch:<br>
        Telefon: ${htmlEscape(state.settings.restaurantPhone || '—')}</p>
      <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück zur Startseite</a>`;
    return;
  }
  // Order-Pfad ---------------------------------------------------------------
  if (hit.kind === 'order') {
    const o = hit.data;
    if (['cancelled','picked_up','no_show'].includes(o.status)) {
      wrap.innerHTML = `
        <h2>Bestellung nicht stornierbar</h2>
        <p>Diese Bestellung ist bereits ${o.status === 'cancelled' ? 'storniert' : (o.status === 'picked_up' ? 'abgeholt' : 'als nicht abgeholt vermerkt')} worden.</p>
        <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück zur Startseite</a>`;
      return;
    }
    if (['preparing','ready'].includes(o.status)) {
      wrap.innerHTML = `
        <h2>Bestellung ist schon in Arbeit</h2>
        <p>Deine Bestellung Nr. ${htmlEscape(o.orderNumber)} ist bereits ${o.status === 'preparing' ? 'in Zubereitung' : 'abholbereit'}. Online-Stornierung ist jetzt nicht mehr möglich — bitte ruf uns an:</p>
        <p style="color:var(--ink-soft);">Telefon: ${htmlEscape(state.settings.restaurantPhone || '—')}</p>
        <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück</a>`;
      return;
    }
    wrap.innerHTML = `
      <h2>Bestellung stornieren</h2>
      <p>Möchtest du folgende Bestellung wirklich stornieren?</p>
      <div style="background:var(--bg-soft); padding:14px; border-radius:8px; margin: 12px 0;">
        <div><strong>Bestellung Nr. ${htmlEscape(o.orderNumber)}</strong></div>
        <div>${htmlEscape(o.customerName)}</div>
        <div>Abholung: ${fmtDate(o.pickupDate)}, ${o.pickupTime} Uhr</div>
        <div>Gesamt: ${fmtEUR((Number(o.totalGrossCents) || 0) / 100)}</div>
      </div>
      <div class="btn-row">
        <button class="btn btn-danger" onclick="performCustomerCancel('${htmlEscape(token)}','order')">Ja, stornieren</button>
        <button class="btn btn-secondary" onclick="closeCancelView()">Doch nicht</button>
      </div>`;
    return;
  }
  // Reservation-Pfad ---------------------------------------------------------
  const r = hit.data;
  if (r.status === 'cancelled' || r.status === 'declined') {
    wrap.innerHTML = `
      <h2>Bereits storniert</h2>
      <p>Diese Reservierung ist bereits ${r.status === 'cancelled' ? 'storniert' : 'abgesagt'} worden.</p>
      <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück zur Startseite</a>`;
    return;
  }
  wrap.innerHTML = `
    <h2>Reservierung stornieren</h2>
    <p>Möchtest du folgende Reservierung wirklich stornieren?</p>
    <div style="background:var(--bg-soft); padding:14px; border-radius:8px; margin: 12px 0;">
      <div><strong>${htmlEscape(r.name)}</strong></div>
      <div>${fmtDate(r.date)}, ${r.time} Uhr</div>
      <div>${r.guests} ${r.guests === 1 ? 'Person' : 'Personen'}</div>
    </div>
    <div class="btn-row">
      <button class="btn btn-danger" onclick="performCustomerCancel('${htmlEscape(token)}','reservation')">Ja, stornieren</button>
      <button class="btn btn-secondary" onclick="closeCancelView()">Doch nicht</button>
    </div>
  `;
}

/** Storno-RPC aufrufen; idempotent. Der zweite Parameter sagt uns, ob es
 *  eine Bestellung oder eine Reservierung ist. Default-Fallback (keine
 *  Angabe) probiert beide. */
export async function performCustomerCancel(token, kindHint) {
  if (!token) return;
  const kind = kindHint || tokenKind(token);
  try {
    let result = null;
    if (kind === 'order') {
      // Order-Header vorher laden, damit das Cancel-Event Name/Bestellnummer/
      // Pickup-Zeit kennt (das cancel-RPC liefert nur id/status/cancelledAt).
      // Email ist anon nicht sichtbar — Customer-Cancel-Mail wird daher
      // serverseitig nachgereicht (Phase 9 / Outbox-Pattern).
      let header = null;
      try { header = await window.App.storage.orders.getByToken(token); } catch (_) {}
      result = await window.App.storage.orders.cancelByToken(token);
      if (!result) throw new Error('Bestellung nicht gefunden.');
      const merged = Object.assign({}, header || {}, result);
      if (window.App.events) {
        await window.App.events.emit('order:cancelled', { order: merged, by: 'customer' });
      }
      document.getElementById('cancel-card').innerHTML = `
        <h2>Bestellung storniert</h2>
        <p>Deine Bestellung wurde storniert. Vielleicht beim nächsten Mal — wir würden uns freuen!</p>
        <a class="btn btn-primary" href="#" onclick="closeCancelView();return false;">Zur Startseite</a>`;
    } else {
      result = await window.App.storage.reservations.cancelByToken(token);
      if (!result) throw new Error('Reservierung nicht gefunden.');
      if (window.App.events) {
        await window.App.events.emit('reservation:cancelled', { reservation: result, by: 'customer' });
      }
      document.getElementById('cancel-card').innerHTML = `
        <h2>Storniert</h2>
        <p>Deine Reservierung wurde storniert. Wir würden uns freuen, dich bald wieder bei uns begrüßen zu dürfen!</p>
        <a class="btn btn-primary" href="#" onclick="closeCancelView();return false;">Neue Reservierung</a>`;
    }
  } catch (e) {
    console.error('[performCustomerCancel]', e);
    const friendly = _friendlyCancelError(e);
    const card = document.getElementById('cancel-card');
    if (friendly) {
      // S75 Storno-Härtung: Order/Reservierung ist serverseitig gesperrt
      // (in Zubereitung / unterwegs) — freundlicher Text statt Raw-Exception.
      card.innerHTML = `
        <h2>Storno nicht mehr möglich</h2>
        <p>${htmlEscape(friendly)}</p>
        <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück</a>`;
    } else {
      card.innerHTML = `
        <h2>Stornierung fehlgeschlagen</h2>
        <p>Wir konnten den Vorgang gerade nicht stornieren. Bitte ruf uns kurz an, dann erledigen wir das telefonisch.</p>
        <p style="color: var(--ink-soft); font-size:.9rem;">Fehler: ${htmlEscape(e.message || String(e))}</p>
        <a class="btn btn-secondary" href="#" onclick="closeCancelView();return false;">Zurück</a>`;
    }
  }
}

/** Cancel-View schließen und zurück zum Customer-Form. */
export function closeCancelView() {
  history.replaceState(null, '', window.location.pathname + window.location.search);
  if (typeof window.switchView === 'function') window.switchView('customer');
}

// ---------------------------------------------------------------
// Pure helpers (extrahiert in Session 39 / B.3 für Test-Coverage)
// ---------------------------------------------------------------

/**
 * Parsed das `#cancel=<token>` aus dem URL-Hash. Defensiv gegen URL-Encoding,
 * leeren Hash und malformierte Inputs.
 * @param {string} hash window.location.hash
 * @returns {string|null} Decoded token oder null
 */
function _parseCancelTokenFromHash(hash) {
  if (typeof hash !== 'string' || hash.length === 0) return null;
  const m = hash.match(/^#cancel=(.+)$/);
  if (!m) return null;
  try {
    const decoded = decodeURIComponent(m[1]);
    if (!decoded || decoded.length === 0) return null;
    return decoded;
  } catch (_) {
    // malformed %-Sequenzen → null statt Throw
    return null;
  }
}

/**
 * Entscheidet, ob eine Order über den Customer-Cancel-Link storniert werden
 * darf. GoBD-nah: vor Submit zeigen wir nur die Card; die echte Mutation
 * macht `performCustomerCancel` → `cancel_order_by_token`-RPC.
 * @param {{status?: string}} order
 * @returns {'blocked_completed' | 'blocked_in_progress' | 'ok'}
 */
function _decideOrderCancelability(order) {
  const status = (order && order.status) || '';
  if (status === 'cancelled' || status === 'picked_up' || status === 'no_show') {
    return 'blocked_completed';
  }
  if (status === 'preparing' || status === 'ready') {
    return 'blocked_in_progress';
  }
  return 'ok';
}

/**
 * Entscheidet, ob eine Reservation über den Customer-Cancel-Link storniert
 * werden darf.
 * @param {{status?: string}} reservation
 * @returns {'blocked_already' | 'ok'}
 */
function _decideReservationCancelability(reservation) {
  const status = (reservation && reservation.status) || '';
  if (status === 'cancelled' || status === 'declined') return 'blocked_already';
  return 'ok';
}

/**
 * Mappt die serverseitigen Storno-Sperr-Codes (cancel_order_by_token /
 * cancel_reservation_by_token, phase_74 S75) auf freundliche Kundentexte.
 * Liefert '' wenn kein Sperr-Code matcht (→ generische Fehlermeldung).
 * @param {Error|string} e
 * @returns {string}
 */
function _friendlyCancelError(e) {
  const msg = (e && e.message) || String(e || '');
  if (msg.includes('cancel_locked:kitchen_active')) {
    return 'Deine Bestellung wird bereits zubereitet und kann nicht mehr online storniert werden. Bitte melde dich direkt im Restaurant.';
  }
  if (msg.includes('cancel_locked:delivery_active')) {
    return 'Deine Bestellung ist bereits unterwegs zu dir und kann nicht mehr online storniert werden. Bitte melde dich direkt im Restaurant.';
  }
  if (msg.includes('cancel_locked:preorder_active')) {
    return 'Zu deiner Reservierung wird bereits Essen vorbereitet — eine Online-Stornierung ist nicht mehr möglich. Bitte melde dich direkt im Restaurant.';
  }
  return '';
}

// Alias-Export von tokenKind als Test-Hook (kein Verhaltens-Bruch).
const _tokenKind = tokenKind;

// Backward-Compat
if (typeof window !== 'undefined') {
  Object.assign(window, {
    checkCancelRequestFromUrl,
    renderCancelLoading,
    renderCancelView,
    performCustomerCancel,
    closeCancelView
  });
}

// ---------------------------------------------------------------
// Test-Hook-Exports (Session 39, Pattern aus customer-form.js).
// Zur Laufzeit No-Op.
// ---------------------------------------------------------------
export {
  _parseCancelTokenFromHash,
  _decideOrderCancelability,
  _decideReservationCancelability,
  _friendlyCancelError,
  _tokenKind,
};
